import { enqueueSnackbar } from 'notistack'
import {
  ExamSubject,
  SubmitExamGradingAnswer,
  SubmitExamGradingAnswerItem,
  TentativeScoringQuestionItemDesktop_ExamGradingQuestionFragment,
} from 'generated/graphql'
import {
  TentativeScoringQuestionInputQuestionsType,
  TentativeScoringQuestionListFormType,
} from '../types/tentativeScoringQuestionListForm.type'

type ParseFormDataFn = (
  inputQuestions: Array<TentativeScoringQuestionInputQuestionsType>,
  subject: ExamSubject,
) => SubmitExamGradingAnswer

type CheckSubmitDataFn = (
  data: TentativeScoringQuestionListFormType,
  isMobile?: boolean,
) => {
  isConfirmData: boolean
  submitData: Array<SubmitExamGradingAnswer> | null
}

/**
 * @description 가채점 데이터를 mutation input 데이터에 맞도록 변환하여 반환합니다.
 * @param inputQuestions 가채점 데이터
 * @returns
 */
export const parseFormData: ParseFormDataFn = (inputQuestions, subject) => {
  const questionList: Array<SubmitExamGradingAnswerItem> = inputQuestions
    .map((question) => {
      return {
        questionId: question.questionId,
        answers: [parseInt(question.selectedAnswer ?? '0', 10)],
      }
    })
    .filter((question) => question.answers[0] !== 0)

  return {
    subject,
    submitAnswerItems: [...questionList],
  }
}

/**
 * @description 제출 데이터를 확인하고, 제출 가능 여부를 반환합니다.
 * 입력된 항목이 0개면 제출불가, 1개만 입력해도 제출가능
 * 전체입력된답안이0개일때
 * - 체크한 문항이 없습니다. 답을 체크한 후 진행해 주세요.
 *
 * 수리영역에만 입력된 답안이 0개일 경우
 * - 수리영역 항목에 입력한 답안이 없습니다. 그래도 진행하시겠습니까?
 *
 * 추리영역에만 입력된 답안이 0개일 경우
 * - 추리영역 항목에 입력한 답안이 없습니다. 그래도 진행하시겠습니까?
 * @param data 가채점 입력 데이터
 */
export const checkSubmitData: CheckSubmitDataFn = (data, isMobile = false) => {
  const mathAnswerList = data?.math?.inputQuestions.filter(
    (question) => !!question.selectedAnswer,
  )
  const reasoningAnswerList = data?.reasoning?.inputQuestions.filter(
    (question) => !!question.selectedAnswer,
  )

  if (mathAnswerList?.length === 0 && reasoningAnswerList?.length === 0) {
    if (isMobile) {
      enqueueSnackbar('체크한 문항이 없습니다. 답을 체크한 후 진행해 주세요.', {
        style: {
          borderRadius: '20px',
        },
      })
    } else {
      alert('체크한 문항이 없습니다. 답을 체크한 후 진행해 주세요.')
    }
    return {
      isConfirmData: false,
      submitData: null,
    }
  } else if (mathAnswerList?.length === 0) {
    return {
      isConfirmData: confirm(
        '수리영역 항목에 입력한 답안이 없습니다. 그래도 진행하시겠습니까?',
      ),
      submitData: [
        parseFormData(reasoningAnswerList ?? [], ExamSubject.REASONING),
      ],
    }
  } else if (reasoningAnswerList?.length === 0) {
    return {
      isConfirmData: confirm(
        '추리영역 항목에 입력한 답안이 없습니다. 그래도 진행하시겠습니까?',
      ),
      submitData: [parseFormData(mathAnswerList ?? [], ExamSubject.MATH)],
    }
  } else {
    return {
      isConfirmData: true,
      submitData: [
        parseFormData(mathAnswerList ?? [], ExamSubject.MATH),
        parseFormData(reasoningAnswerList ?? [], ExamSubject.REASONING),
      ],
    }
  }
}

export const tentativeScoringQuestionListDefaultValues = (
  examGradingQuestions?: Array<TentativeScoringQuestionItemDesktop_ExamGradingQuestionFragment>,
): Array<TentativeScoringQuestionInputQuestionsType> => {
  if (examGradingQuestions) {
    const questionList = examGradingQuestions.map((question) => {
      return {
        questionId: question.id,
        selectedAnswer: question.userAnswers[0]
          ? question.userAnswers[0].toString()
          : null,
      }
    })

    return [...questionList]
  } else {
    return []
  }
}
